"use client";

import {
  CalendarCheck2,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Globe2,
  LoaderCircle,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type FormEvent,
} from "react";

import { CcBookCallTimeZoneSelect } from "@/components/booking/CcBookCallTimeZoneSelect";
import {
  bookCallIntlLocale,
  calendarStartsOnSunday,
  dateKeyFromParts,
  dateKeyInTimeZone,
  formatBookCallDateTime,
  formatBookCallFullDate,
  formatBookCallMonthYear,
  formatBookCallSlotTime,
  formatBookCallWeekdayDate,
  formatBookCallWeekdayShort,
  monthGrid,
  prefers12HourClock,
} from "@/lib/booking/book-call-datetime";
import {
  clearBookingAvailabilityCache,
  invalidateBookingAvailabilityMonth,
  loadBookingAvailability,
  removeSlotFromBookingSlotMap,
  type BookingSlotMap,
} from "@/lib/booking-availability-client";
import type {
  BookCallFormCopyOverrides,
  BookCallFormPrefill,
  BookCallPopupVariant,
} from "@/lib/book-call-popup-copy";
import { getBookingFormCopy } from "@/lib/booking/booking-form-copy";
import type { Lang } from "@/lib/types";
import { emitAnalyticsEvent } from "@/lib/analytics-events";
import { trackBookingLead } from "@/lib/contact-lead-tracking";

type BookingStep = "schedule" | "details" | "confirmed";

const subscribeToTimeZone = () => () => {};
const browserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Stockholm";
const serverTimeZone = () => "Europe/Stockholm";

export function CcCustomCalBooking({
  lang,
  formCopyOverrides,
  formPrefill,
  bookingVariant,
  onBooked,
}: {
  lang: Lang;
  formCopyOverrides?: BookCallFormCopyOverrides;
  formPrefill?: BookCallFormPrefill;
  bookingVariant: BookCallPopupVariant;
  /** Called once the booking is confirmed (e.g. to record a conversion). */
  onBooked?: () => void;
}) {
  const locale = bookCallIntlLocale(lang);
  const baseCopy =
    getBookingFormCopy(lang);
  const copy = { ...baseCopy, ...formCopyOverrides };
  const websiteOnlyMode = formCopyOverrides?.notes === "";

  const initialInstant = new Date();
  const [visibleMonth, setVisibleMonth] = useState(() => ({
    year: initialInstant.getFullYear(),
    monthIndex: initialInstant.getMonth(),
  }));
  const [slots, setSlots] = useState<BookingSlotMap>({});
  const [selectedDate, setSelectedDate] = useState(() =>
    dateKeyInTimeZone(initialInstant, browserTimeZone()),
  );
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(false);
  const [availabilityRequest, setAvailabilityRequest] = useState(0);
  const [bookingPending, setBookingPending] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [appointmentId, setAppointmentId] = useState("");
  const [checkingSlot, setCheckingSlot] = useState<string | null>(null);
  const [slotCheckError, setSlotCheckError] = useState("");
  const detectedTimeZone = useSyncExternalStore(
    subscribeToTimeZone,
    browserTimeZone,
    serverTimeZone,
  );
  const [selectedTimeZone, setSelectedTimeZone] = useState<string | null>(null);
  const [hourModeOverride, setHourModeOverride] = useState<"12" | "24" | null>(
    null,
  );
  const timeZone = selectedTimeZone ?? detectedTimeZone;
  const hourMode =
    hourModeOverride ?? (prefers12HourClock(timeZone) ? "12" : "24");
  const weekStartsOnSunday = calendarStartsOnSunday(timeZone);
  const [step, setStep] = useState<BookingStep>("schedule");
  const [website, setWebsite] = useState(() => formPrefill?.website ?? "");
  const [notes, setNotes] = useState(() => formPrefill?.notes ?? "");

  const todayKey = dateKeyInTimeZone(new Date(), timeZone);
  const todayParts = useMemo(
    () => ({
      year: Number(todayKey.slice(0, 4)),
      monthIndex: Number(todayKey.slice(5, 7)) - 1,
    }),
    [todayKey],
  );
  const isCurrentMonth =
    visibleMonth.year === todayParts.year &&
    visibleMonth.monthIndex === todayParts.monthIndex;
  const cells = useMemo(
    () =>
      monthGrid(
        visibleMonth.year,
        visibleMonth.monthIndex,
        weekStartsOnSunday,
      ),
    [visibleMonth.monthIndex, visibleMonth.year, weekStartsOnSunday],
  );
  const selectedDateLabel = formatBookCallWeekdayDate(
    selectedDate,
    locale,
    timeZone,
  );
  const monthLabel = formatBookCallMonthYear(
    visibleMonth.year,
    visibleMonth.monthIndex,
    locale,
    timeZone,
  );
  const weekdayLabels = formatBookCallWeekdayShort(
    locale,
    timeZone,
    weekStartsOnSunday,
  );
  const applyMonthAvailability = (nextSlots: BookingSlotMap) => {
    const firstAvailableDate = Object.keys(nextSlots)
      .filter((key) => nextSlots[key]?.slots.length)
      .sort()[0];
    setSlots(nextSlots);
    setSelectedDate((current) =>
      nextSlots[current]?.slots.length
        ? current
        : (firstAvailableDate ??
          dateKeyFromParts({
            year: visibleMonth.year,
            month: visibleMonth.monthIndex + 1,
            day: 1,
          })),
    );
    setSelectedSlot(null);
  };

  const refreshMonthAvailabilityInBackground = () => {
    invalidateBookingAvailabilityMonth(visibleMonth, timeZone, lang);
    void loadBookingAvailability(visibleMonth, timeZone, lang, {
      bypassCache: true,
    })
      .then((nextSlots) => applyMonthAvailability(nextSlots))
      .catch(() => undefined);
  };

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (cancelled) return;
      setAvailabilityLoading(true);
      setAvailabilityError(false);
      setSlotCheckError("");
    });
    void loadBookingAvailability(visibleMonth, timeZone, lang)
      .then((nextSlots) => {
        if (cancelled) return;
        applyMonthAvailability(nextSlots);
      })
      .catch(() => {
        if (cancelled) return;
        setSlots({});
        setAvailabilityError(true);
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    availabilityRequest,
    lang,
    timeZone,
    visibleMonth,
  ]);

  const selectSlot = async (slot: string) => {
    if (checkingSlot) return;
    setCheckingSlot(slot);
    setSlotCheckError("");
    try {
      const response = await fetch("/api/booking/availability/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ startTime: slot, timeZone, lang }),
      });
      const payload = (await response.json()) as {
        available?: boolean;
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || copy.loadError);
      if (!payload.available) {
        setSlots((current) => removeSlotFromBookingSlotMap(current, slot));
        setSlotCheckError(copy.noLongerAvailable);
        refreshMonthAvailabilityInBackground();
        return;
      }
      setSelectedSlot(slot);
      setStep("details");
      emitAnalyticsEvent("site_booking_step", { booking_variant: bookingVariant, booking_step: "details", language: lang });
    } catch (error) {
      setSlotCheckError(error instanceof Error ? error.message : copy.loadError);
    } finally {
      setCheckingSlot(null);
    }
  };

  const formatSlot = (slot: string) =>
    formatBookCallSlotTime(slot, locale, timeZone, hourMode);

  const submitBooking = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSlot || bookingPending) return;
    const form = new FormData(event.currentTarget);
    setBookingPending(true);
    setBookingError("");
    try {
      const response = await fetch("/api/booking/appointments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.get("firstName"),
          lastName: form.get("lastName"),
          email: form.get("email"),
          company: form.get("company"),
          website: form.get("website"),
          notes: form.get("notes"),
          pageUrl: window.location.href,
          pageTitle: document.title,
          referrer: document.referrer,
          startTime: selectedSlot,
          timeZone,
          lang,
          bookingVariant,
        }),
      });
      const payload = (await response.json()) as {
        appointment?: { id?: string };
        error?: string;
      };
      if (!response.ok) {
        if (response.status === 409) {
          setStep("schedule");
          setSelectedSlot(null);
          setSlots((current) =>
            selectedSlot
              ? removeSlotFromBookingSlotMap(current, selectedSlot)
              : current,
          );
          setSlotCheckError(copy.noLongerAvailable);
          refreshMonthAvailabilityInBackground();
          return;
        }
        throw new Error(payload.error || copy.bookingError);
      }
      if (typeof payload.appointment?.id !== "string" || !payload.appointment.id) {
        throw new Error(copy.bookingError);
      }
      setAppointmentId(payload.appointment.id);
      setStep("confirmed");
      trackBookingLead(lang, bookingVariant);
      onBooked?.();
    } catch (error) {
      setBookingError(error instanceof Error ? error.message : copy.bookingError);
    } finally {
      setBookingPending(false);
    }
  };

  if (step === "confirmed" && selectedSlot) {
    return (
      <div className="cc-custom-cal-flow cc-custom-cal-flow--centered">
        <div className="cc-custom-cal-confirmation" role="status">
          <span className="cc-custom-cal-confirmation__icon">
            <CircleCheck aria-hidden="true" />
          </span>
          <h3>{copy.confirmed}</h3>
          <p className="cc-custom-cal-confirmation__when">
            {formatBookCallDateTime(
              selectedSlot,
              locale,
              timeZone,
              hourMode,
            )}
          </p>
          <p>{copy.confirmedBody}</p>
          <button
            type="button"
            className="cc-custom-cal__secondary-button"
            onClick={() => {
              setSelectedSlot(null);
              setStep("schedule");
            }}
          >
            {copy.another}
          </button>
          {appointmentId ? (
            <span className="cc-custom-cal-confirmation__mock-id">{appointmentId}</span>
          ) : null}
        </div>
      </div>
    );
  }

  if (step === "details" && selectedSlot) {
    return (
      <div className="cc-custom-cal-flow cc-custom-cal-flow--details">
        <button
          type="button"
          className="cc-custom-cal__back"
          onClick={() => setStep("schedule")}
        >
          <ChevronLeft aria-hidden="true" />
          {copy.back}
        </button>
        <div className="cc-custom-cal-details__heading">
          <span className="cc-custom-cal-details__icon">
            <CalendarCheck2 aria-hidden="true" />
          </span>
          <div>
            <h3>{copy.yourDetails}</h3>
            <p>
              {formatBookCallDateTime(
                selectedSlot,
                locale,
                timeZone,
                hourMode,
              )}
            </p>
          </div>
        </div>
        <form className="cc-custom-cal-form" onSubmit={submitBooking} data-analytics-form="booking" data-book-call-variant={bookingVariant}>
          <div className="cc-custom-cal-form__row">
            <label>
              <span>{copy.firstName} *</span>
              <input
                name="firstName"
                defaultValue={formPrefill?.firstName ?? ""}
                autoComplete="given-name"
                placeholder={copy.firstNamePlaceholder}
                required
              />
            </label>
            <label>
              <span>{copy.lastName} *</span>
              <input
                name="lastName"
                defaultValue={formPrefill?.lastName ?? ""}
                autoComplete="family-name"
                placeholder={copy.lastNamePlaceholder}
                required
              />
            </label>
          </div>
          <label>
            <span>{copy.email} *</span>
            <input
              name="email"
              defaultValue={formPrefill?.email ?? ""}
              type="email"
              autoComplete="email"
              placeholder={copy.emailPlaceholder}
              required
            />
          </label>
          <label>
            <span>{copy.company} *</span>
            <input
              name="company"
              defaultValue={formPrefill?.company ?? ""}
              autoComplete="organization"
              placeholder={copy.companyPlaceholder}
              required
            />
          </label>
          <label>
            <span>{copy.website}</span>
            <input
              name="website"
              // Plain text so "exempel.se" passes; the API adds https://.
              type="text"
              inputMode="url"
              autoComplete="url"
              placeholder={
                websiteOnlyMode ? copy.notesPlaceholder : copy.websitePlaceholder
              }
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </label>
          {!websiteOnlyMode ? (
            <label>
              <span>{copy.notes}</span>
              <textarea
                name="notes"
                placeholder={copy.notesPlaceholder}
                rows={3}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>
          ) : null}
          {bookingError ? (
            <p className="cc-custom-cal-times__slot-error" role="alert">
              {bookingError}
            </p>
          ) : null}
          <button
            type="submit"
            className="cc-custom-cal__primary-button"
            disabled={bookingPending}
          >
            {bookingPending ? copy.booking : copy.confirm}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="cc-custom-cal-flow">
      <section className="cc-custom-cal-calendar" aria-label={copy.choose}>
        <div className="cc-custom-cal-calendar__header">
          <div>
            <h3>{monthLabel}</h3>
          </div>
          <div className="cc-custom-cal-calendar__controls">
            {!isCurrentMonth ? (
              <button
                type="button"
                className="cc-custom-cal-calendar__today"
                onClick={() =>
                  setVisibleMonth({
                    year: todayParts.year,
                    monthIndex: todayParts.monthIndex,
                  })
                }
              >
                {copy.today}
              </button>
            ) : null}
            <button
              type="button"
              aria-label={copy.previousMonth}
              onClick={() =>
                setVisibleMonth((current) => {
                  const monthIndex = current.monthIndex - 1;
                  if (monthIndex >= 0) {
                    return { year: current.year, monthIndex };
                  }
                  return { year: current.year - 1, monthIndex: 11 };
                })
              }
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={copy.nextMonth}
              onClick={() =>
                setVisibleMonth((current) => {
                  const monthIndex = current.monthIndex + 1;
                  if (monthIndex <= 11) {
                    return { year: current.year, monthIndex };
                  }
                  return { year: current.year + 1, monthIndex: 0 };
                })
              }
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="cc-custom-cal-calendar__weekdays" aria-hidden="true">
          {weekdayLabels.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="cc-custom-cal-calendar__grid">
          {cells.map((cell, index) => {
            if (!cell) return <span key={`empty-${index}`} />;
            const key = dateKeyFromParts({
              year: visibleMonth.year,
              month: visibleMonth.monthIndex + 1,
              day: cell.day,
            });
            const isAvailable = Boolean(slots[key]?.slots.length);
            const isSelected = key === selectedDate;
            return (
              <button
                key={key}
                type="button"
                className={isSelected ? "is-selected" : ""}
                disabled={!isAvailable}
                aria-pressed={isSelected}
                aria-label={formatBookCallFullDate(key, locale, timeZone)}
                onClick={() => {
                  setSelectedDate(key);
                  setSelectedSlot(null);
                  setSlotCheckError("");
                }}
              >
                {cell.day}
                {isAvailable ? <span aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
      </section>
      <section className="cc-custom-cal-times" aria-live="polite">
        <div className="cc-custom-cal-times__header">
          <div>
            <p className="cc-custom-cal-times__eyebrow">{copy.availableTimes}</p>
            <h3>{selectedDateLabel}</h3>
          </div>
          <div className="cc-custom-cal-times__format" role="group" aria-label={copy.timeFormat}>
            <button
              type="button"
              className={hourMode === "12" ? "is-active" : ""}
              aria-pressed={hourMode === "12"}
              onClick={() => setHourModeOverride("12")}
            >
              {copy.format12}
            </button>
            <button
              type="button"
              className={hourMode === "24" ? "is-active" : ""}
              aria-pressed={hourMode === "24"}
              onClick={() => setHourModeOverride("24")}
            >
              {copy.format24}
            </button>
          </div>
        </div>
        <label className="cc-custom-cal-times__timezone">
          <Globe2 aria-hidden="true" />
          <CcBookCallTimeZoneSelect
            locale={locale}
            value={timeZone}
            ariaLabel={copy.timezone}
            searchPlaceholder={copy.timezoneSearch}
            emptyLabel={copy.timezoneEmpty}
            onChange={(nextTimeZone) => {
              setSelectedTimeZone(nextTimeZone);
              setHourModeOverride(null);
            }}
          />
        </label>
        {availabilityLoading ? (
          <div
            className="cc-custom-cal-times__loading"
            role="status"
            aria-label={copy.loading}
          >
            <LoaderCircle aria-hidden="true" />
          </div>
        ) : availabilityError ? (
          <div className="cc-custom-cal-times__empty" role="alert">
            <p>{copy.loadError}</p>
            <button
              type="button"
              className="cc-custom-cal__secondary-button"
              onClick={() => {
                clearBookingAvailabilityCache();
                setAvailabilityRequest((value) => value + 1);
              }}
            >
              {copy.today}
            </button>
          </div>
        ) : slots[selectedDate]?.slots.length ? (
          <>
            {slotCheckError ? (
              <p className="cc-custom-cal-times__slot-error" role="alert">
                {slotCheckError}
              </p>
            ) : null}
            <div className="cc-custom-cal-times__list" data-lenis-prevent>
              {slots[selectedDate].slots.map((slot) => {
                const isChecking = checkingSlot === slot;
                return (
                  <button
                    key={slot}
                    type="button"
                    disabled={checkingSlot !== null}
                    aria-label={isChecking ? copy.checking : undefined}
                    onClick={() => void selectSlot(slot)}
                  >
                    <span aria-hidden="true" />
                    {isChecking ? (
                      <LoaderCircle
                        className="cc-custom-cal-times__slot-spinner"
                        aria-hidden="true"
                      />
                    ) : (
                      formatSlot(slot)
                    )}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <p className="cc-custom-cal-times__empty">{copy.selectDate}</p>
        )}
      </section>
    </div>
  );
}
